// RichText — the read-only renderer for typed inline content, pure.
//
// The editor's inline level is NOT a markdown string: a block's text is a tree
// of typed inline nodes (rich-content-and-media.md §B.1, §D.4). This component
// is the reading face of that tree — it maps each typed node to its concrete
// React element via a total match over the `type` discriminant. That is the
// whole difference from Prose (which parses a markdown STRING): here the shape
// is already typed, so there is nothing to parse and no sanitiser to trust —
// the model can only express the inline kinds the sum admits.
//
// Colour is NEVER the sole channel (SC 1.4.1). Links carry an underline, not a
// tint (the accent is reserved for the primary action / agent identity, so body
// links are never recoloured — the same rule Prose applies). The `ref` token is
// set apart by a subtle background fill (a shape channel, not a hue) and a
// VisuallyHidden "Reference:" prefix that names it for assistive tech, the same
// discipline DiffMark uses for insertions/deletions.
//
// Pure/props-driven: `content` is the only input. No state, no effects, no I/O,
// no domain knowledge — the render is a total function of the tree it is
// handed. Every value comes from tokens via RichText.css, scoped to
// .bilda-rich-text so it composes inline anywhere without leaking.
//
// SHAPE-FOR-POST-MVP: the `ref` node here carries only a display `label` (see
// types.ts) — the real domain coupling (a PartResourceRef plus grounding state)
// is deferred, so the token is display-only for now.

import { exhaustSwitchCase } from "@bilda/utils";
import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { VisuallyHidden } from "../../VisuallyHidden/VisuallyHidden.js";
import type { InlineNode, RichText as RichTextTree } from "../types.js";
import "./RichText.css";

export type RichTextProps = {
    readonly content: RichTextTree;
};

// Render one inline node to its concrete element. A branch per variant keeps
// each host element concrete (`<strong>`, `<em>`, `<a>`, `<code>`, `<br>`) — no
// dynamic tag, no cast; narrowing is by the `type` discriminant alone. `index`
// is the array position, used as the React key: safe here because the content
// is static and read-only (no insert/remove/reorder), so positions are stable.
const renderNode = (node: InlineNode, index: number): ReactNode => {
    switch (node.type) {
        case "text":
            // A bare text run — a string is a valid ReactNode and needs no key.
            return node.text;
        case "strong":
            return <strong key={index}>{node.children.map(renderNode)}</strong>;
        case "emphasis":
            return <em key={index}>{node.children.map(renderNode)}</em>;
        case "link":
            // Leaves the app: new tab + severed opener (reverse-tabnabbing
            // guard). The underline is the visible channel, set in CSS — the
            // accent is not spent on body links.
            return (
                <a
                    key={index}
                    href={node.href}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {node.children.map(renderNode)}
                </a>
            );
        case "code":
            return (
                <code key={index} className="bilda-rich-text__code">
                    {node.text}
                </code>
            );
        case "break":
            return <br key={index} />;
        case "ref":
            // A quiet reference token: the subtle fill (a shape channel, not a
            // hue) sets it apart from body text, and the VisuallyHidden prefix
            // names it a reference — plus its `label` — for assistive tech. The
            // wrapped children stay the visible token text.
            return (
                <span key={index} className="bilda-rich-text__ref">
                    <VisuallyHidden>
                        <Trans>Reference:</Trans> {node.label}{" "}
                    </VisuallyHidden>
                    {node.children.map(renderNode)}
                </span>
            );
        default:
            // Exhaustiveness: a new InlineNode variant fails to compile here
            // until it gets an arm above (the house convention).
            return exhaustSwitchCase(node);
    }
};

export const RichText = ({ content }: RichTextProps) => (
    <span className="bilda-rich-text">{content.map(renderNode)}</span>
);
